import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  PackageMinus,
  Plus,
  Save,
  Search,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import api from '../config/api';
import SearchableSelect from '../components/SearchableSelect';
import { useNavigate } from 'react-router-dom';

const reasons = ['Broken Pack', 'Expired/Damaged', 'Leakage', 'Wrong Storage', 'Other'];
const PAGE_SIZE = 50;

export default function DamageAlerts() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [medicines, setMedicines] = useState([]);
  const [batches, setBatches] = useState([]);
  const [damages, setDamages] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    medicine_id: '',
    batch_id: '',
    damage_date: today,
    unit_name: '',
    quantity: '',
    reason: reasons[0],
    notes: '',
  });

  useEffect(() => {
    loadSupportData();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => loadDamages(), search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [page, search, refreshKey]);

  const loadSupportData = async () => {
    const [medRes, batchRes] = await Promise.allSettled([
      api.get('/medicines?limit=500'),
      api.get('/batches'),
    ]);
    if (medRes.status === 'fulfilled') {
      setMedicines(Array.isArray(medRes.value) ? medRes.value : (medRes.value?.medicines || []));
    } else {
      toast.error('Failed to load medicines');
    }
    if (batchRes.status === 'fulfilled') {
      setBatches(Array.isArray(batchRes.value) ? batchRes.value : []);
    }
  };

  const handleSearchMedicines = async (query) => {
    const q = (query || '').trim();
    if (!q) return;
    try {
      const res = await api.get(`/medicines?search=${encodeURIComponent(q)}&limit=100`);
      const foundMeds = Array.isArray(res) ? res : (res?.medicines || []);
      if (foundMeds.length > 0) {
        setMedicines((prev) => {
          const map = new Map(prev.map((m) => [String(m.id), m]));
          for (const m of foundMeds) {
            map.set(String(m.id), m);
          }
          return Array.from(map.values());
        });
      }
    } catch (err) {
      console.error('Failed to search medicines in DamageAlerts', err);
    }
  };

  const loadDamages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (search.trim()) params.set('search', search.trim());
      const result = await api.get(`/damages?${params.toString()}`);
      const list = Array.isArray(result) ? result : (result?.damages || []);
      const pagination = result?.pagination;
      if (pagination?.pages && page > pagination.pages) {
        setPage(pagination.pages);
        return;
      }
      setDamages(list);
      setTotal(pagination?.total ?? list.length);
      setPages(pagination?.pages ?? 1);
    } catch (err) {
      toast.error(err.message || 'Failed to load damaged stock');
    } finally {
      setLoading(false);
    }
  };

  const selectedMedicine = useMemo(() => {
    return medicines.find((m) => String(m.id) === String(form.medicine_id));
  }, [medicines, form.medicine_id]);

  const activeBatches = useMemo(() => {
    return batches.filter((b) => String(b.medicine_id) === String(form.medicine_id) && Number(b.quantity || 0) > 0);
  }, [batches, form.medicine_id]);

  const selectedBatch = useMemo(() => {
    return batches.find((b) => String(b.id) === String(form.batch_id));
  }, [batches, form.batch_id]);

  const availableUnits = useMemo(() => {
    if (!selectedMedicine) return [];
    if (Array.isArray(selectedMedicine.units) && selectedMedicine.units.length > 0) {
      return selectedMedicine.units;
    }
    return [
      {
        unit_name: selectedMedicine.unit || 'pcs',
        conversion_factor: 1,
        is_base_unit: true,
      },
    ];
  }, [selectedMedicine]);

  const selectedUnit = useMemo(() => {
    if (!availableUnits.length) {
      return { unit_name: selectedMedicine?.unit || 'pcs', conversion_factor: 1 };
    }
    return availableUnits.find((u) => u.unit_name === form.unit_name) || availableUnits[0];
  }, [availableUnits, form.unit_name, selectedMedicine]);

  const conversionFactor = Math.max(1, Number(selectedUnit?.conversion_factor || 1));
  const baseUnitName = selectedMedicine?.unit || availableUnits[availableUnits.length - 1]?.unit_name || 'units';

  const totalDamagedQty = damages.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  const openAdd = () => {
    setForm({
      medicine_id: '',
      batch_id: '',
      damage_date: today,
      unit_name: '',
      quantity: '',
      reason: reasons[0],
      notes: '',
    });
    setShowAdd(true);
  };

  const handleMedicineChange = (medicineId) => {
    const med = medicines.find((m) => String(m.id) === String(medicineId));
    const units = med?.units || [];
    const defaultUnit = units.length > 0 ? units[0].unit_name : (med?.unit || 'pcs');
    setForm((prev) => ({
      ...prev,
      medicine_id: medicineId,
      batch_id: '',
      unit_name: defaultUnit,
      quantity: '',
    }));
  };

  const saveDamage = async (e) => {
    e.preventDefault();
    const qty = parseInt(form.quantity);
    if (!form.medicine_id || !form.batch_id) {
      toast.error('Please select medicine and batch');
      return;
    }
    if (!qty || qty <= 0) {
      toast.error('Enter a valid damaged quantity');
      return;
    }

    const totalBaseQty = qty * conversionFactor;
    if (selectedBatch && totalBaseQty > Number(selectedBatch.quantity || 0)) {
      toast.error(
        `Damaged quantity (${totalBaseQty} ${baseUnitName}) cannot exceed available batch stock (${selectedBatch.quantity} ${baseUnitName})`
      );
      return;
    }

    setSaving(true);
    try {
      await api.post('/damages', {
        medicine_id: parseInt(form.medicine_id),
        batch_id: parseInt(form.batch_id),
        damage_date: form.damage_date,
        quantity: totalBaseQty,
        unit_name: selectedUnit.unit_name,
        unit_quantity: qty,
        conversion_factor: conversionFactor,
        reason: form.reason,
        notes: form.notes,
        user_id: 1,
      });
      toast.success('Damaged stock recorded');
      setShowAdd(false);
      await loadSupportData();
      setPage(1);
      setRefreshKey((key) => key + 1);
    } catch (err) {
      toast.error(err.message || 'Failed to record damaged stock');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search damaged stock by medicine, batch, or reason..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 rounded-lg bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" />
          Add Damage
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Damage Entries</p>
          <p className="mt-1 text-3xl font-extrabold text-gray-900">{total.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Damaged Quantity</p>
          <p className="mt-1 text-3xl font-extrabold text-rose-600">{totalDamagedQty}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Latest Damage</p>
          <p className="mt-2 text-sm font-semibold text-gray-900">{damages[0]?.medicine_name || 'No damage recorded'}</p>
          <p className="mt-1 text-xs text-gray-400">{damages[0]?.damage_date || 'Inventory is clear'}</p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-500" />
            <h2 className="text-base font-semibold text-gray-900">Damage Alerts</h2>
          </div>
          <span className="rounded-full bg-rose-100 px-3 py-1 text-[11px] font-bold text-rose-700">
            {total.toLocaleString()} records
          </span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">Loading damage records...</div>
        ) : damages.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="sticky-th-left px-6 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Medicine</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Batch</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Quantity</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {damages.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-gray-50/50">
                    <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-semibold text-gray-900">{item.medicine_name}</p>
                      <p className="text-xs text-gray-400">{item.generic_name || 'No generic name'}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {item.batch_number || '-'}
                      {item.expiry_date ? <span className="ml-2 text-xs text-gray-400">Exp {item.expiry_date}</span> : null}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.damage_date}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-700 w-fit">
                          {item.unit_quantity != null ? item.unit_quantity : item.quantity} {item.unit_name || item.unit || 'pcs'}{(Number(item.unit_quantity != null ? item.unit_quantity : item.quantity) > 1 && !(item.unit_name || item.unit || '').toLowerCase().endsWith('s')) ? 's' : ''}
                        </span>
                        {item.unit_name && Number(item.conversion_factor) > 1 && Number(item.quantity) !== Number(item.unit_quantity) && (
                          <span className="text-[10px] text-gray-400 mt-0.5 pl-0.5 font-medium">
                            (= {item.quantity} {item.unit || 'base units'})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <p className="font-semibold text-gray-900">{item.reason}</p>
                      {item.notes ? <p className="mt-0.5 text-xs text-gray-400">{item.notes}</p> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <PackageMinus className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No damaged stock records</p>
            <p className="mt-1 text-xs text-gray-400">Damaged items will appear here after they are recorded</p>
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
            <p className="text-sm text-gray-500">Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1 || loading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
              <span className="text-sm font-medium text-gray-600">Page {page} of {pages}</span>
              <button type="button" onClick={() => setPage((current) => Math.min(pages, current + 1))} disabled={page >= pages || loading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => navigate('/medicines')}
        className="flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
      >
        View All Medicines <ArrowRight className="h-4 w-4" />
      </button>

      {showAdd &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Record Damaged Stock</h3>
                  <p className="text-xs text-gray-500">Damaged quantity will be deducted from the selected batch according to unit.</p>
                </div>
                <button onClick={() => setShowAdd(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={saveDamage} className="space-y-4 px-6 py-5">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Medicine *</label>
                    <SearchableSelect
                      value={form.medicine_id ? String(form.medicine_id) : ''}
                      onChange={(value) => handleMedicineChange(value)}
                      options={medicines.map((m) => ({
                        value: String(m.id),
                        label: m.name,
                        sublabel: `${m.generic_name ? `${m.generic_name} - ` : ''}Stock: ${m.total_stock || 0} ${m.unit || 'units'}`,
                        keywords: `${m.name} ${m.generic_name || ''} ${m.barcode || ''} ${m.sku || ''}`,
                      }))}
                      placeholder="Select a medicine..."
                      searchPlaceholder="Search medicine name, generic, barcode..."
                      onSearch={handleSearchMedicines}
                      required
                    />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Batch *</label>
                  <select
                    value={form.batch_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, batch_id: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:bg-white"
                    required
                    disabled={!form.medicine_id}
                  >
                    <option value="">{form.medicine_id ? 'Select available batch' : 'Select medicine first'}</option>
                    {activeBatches.map((batch) => {
                      const batchQty = Number(batch.quantity || 0);
                      const inUnits = conversionFactor > 1 ? Math.floor(batchQty / conversionFactor) : batchQty;
                      return (
                        <option key={batch.id} value={batch.id}>
                          {batch.batch_number} — {batchQty} {baseUnitName} available{conversionFactor > 1 ? ` (max ~${inUnits} ${selectedUnit.unit_name}s)` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Unit *
                    </label>
                    <select
                      value={selectedUnit?.unit_name || ''}
                      onChange={(e) => setForm((prev) => ({ ...prev, unit_name: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:bg-white"
                      disabled={!form.medicine_id}
                      required
                    >
                      {availableUnits.map((u) => (
                        <option key={u.unit_name} value={u.unit_name}>
                          {u.unit_name} {u.conversion_factor > 1 ? `(${u.conversion_factor} ${baseUnitName}s)` : `(Base)`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Quantity ({selectedUnit?.unit_name || 'Units'}) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={form.quantity}
                      onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
                      placeholder="0"
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:bg-white"
                      required
                    />
                  </div>
                </div>

                {form.medicine_id && form.quantity && parseInt(form.quantity) > 0 && (
                  <div
                    className={clsx(
                      'rounded-lg p-3 text-xs flex items-center justify-between transition-all',
                      selectedBatch && parseInt(form.quantity) * conversionFactor > Number(selectedBatch.quantity || 0)
                        ? 'bg-rose-50 border border-rose-200 text-rose-700'
                        : 'bg-amber-50 border border-amber-200 text-amber-800'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <PackageMinus className="h-4 w-4 shrink-0" />
                      <span>
                        Deducting <strong>{form.quantity} {selectedUnit?.unit_name}{parseInt(form.quantity) > 1 && !(selectedUnit?.unit_name || '').toLowerCase().endsWith('s') ? 's' : ''}</strong>
                        {conversionFactor > 1 && (
                          <> = <strong className="underline decoration-amber-500 font-bold">{parseInt(form.quantity) * conversionFactor} {baseUnitName}s</strong></>
                        )} from batch stock.
                      </span>
                    </div>
                    {selectedBatch && (
                      <span className="font-semibold text-[11px] shrink-0 ml-2">
                        {Number(selectedBatch.quantity || 0) >= parseInt(form.quantity) * conversionFactor
                          ? `Remaining: ${Number(selectedBatch.quantity || 0) - parseInt(form.quantity) * conversionFactor} ${baseUnitName}s`
                          : `Exceeds stock by ${parseInt(form.quantity) * conversionFactor - Number(selectedBatch.quantity || 0)} ${baseUnitName}s`}
                      </span>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Damage Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        value={form.damage_date}
                        onChange={(e) => setForm((prev) => ({ ...prev, damage_date: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-3 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Reason *</label>
                    <select
                      value={form.reason}
                      onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:bg-white"
                      required
                    >
                      {reasons.map((reason) => <option key={reason}>{reason}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    placeholder="Optional details"
                    className="w-full resize-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:bg-white"
                  />
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAdd(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={clsx('inline-flex items-center gap-2 rounded-lg bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-600', saving && 'cursor-not-allowed opacity-60')}
                  >
                    <Save className="h-4 w-4" />
                    Save Damage
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
